import P from 'prop-types';
// eslint-disable-next-line lodash/import-scope
import get from 'lodash/get';
import Image from '@nerdwallet/react-image';
import { Link } from '@nerdwallet/currency';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import { useState } from 'react';
import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  stringInputSpec,
  stringSelectSpec,
  wrapper,
} from '../../lib/editing-configs';
import { buildSpacingInputs } from '../../lib/universal-components-utils';
import styles from './universal-image.module.less';
import { ImageIcon } from '../../lib/assets/image-icon';

const ImageWrapper = ({ imageLinkUrl, children }) => {
  if (imageLinkUrl) {
    return (
      <Link style={{ width: 'inherit', height: 'inherit' }} href={imageLinkUrl}>
        {children}
      </Link>
    );
  }
  return children;
};

const UniversalImage = ({
  src,
  alt,
  naturalHeight,
  naturalWidth,
  lazy,
  border,
  margin,
  padding,
  backgroundColor,
  objectFit,
  objectPosition,
  imageLinkUrl,
  rolloverImageUrl,
  jumpLink,
  width,
  height,
  outputSample,
  style,
}) => {
  const {
    borderColor,
    borderWidth,
    shadowColor,
    type: borderType,
    borderRounded,
  } = border;

  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const borderColorStyle =
    borderType === 'color'
      ? {
          border: `${borderWidth || 0}px solid ${borderColor || '#cdcdcd'}`,
        }
      : null;

  const borderShadowStyle =
    borderType === 'shadow'
      ? {
          boxShadow: `0px 1px 10px -1px ${shadowColor}`,
        }
      : null;

  const borderRadiusStyle =
    borderType === 'rounded' &&
    borderRounded?.type === 'custom' &&
    borderRounded?.value
      ? {
          borderRadius: borderRounded?.value,
        }
      : null;

  const paddingType = padding?.type || 'currency';
  const marginType = margin?.type || 'currency';

  const currencyBorders =
    borderType === 'rounded' && borderRounded?.type !== 'custom'
      ? borderRounded?.currencyValue
      : 'border-radius-0';

  if (!src && outputSample) {
    return <ImageIcon />;
  }

  if (!src) {
    return null;
  }

  return (
    <div
      id={jumpLink}
      className={classNames([styles.container, classes(currencyBorders)])}
      style={{
        paddingTop: padding?.[paddingType]?.top,
        paddingRight: padding?.[paddingType]?.right,
        paddingBottom: padding?.[paddingType]?.bottom,
        paddingLeft: padding?.[paddingType]?.left,
        marginTop: margin?.[marginType]?.top,
        marginRight: margin?.[marginType]?.right,
        marginBottom: margin?.[marginType]?.bottom,
        marginLeft: margin?.[marginType]?.left,
        ...borderColorStyle,
        ...borderShadowStyle,
        ...borderRadiusStyle,
        ...style,
      }}
    >
      <div
        id="rollover-controller"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          ...(width && { maxWidth: `${width}px` }),
          ...(height && { maxHeight: `${height}px` }),
          margin: 'inherit',
        }}
      >
        <ImageWrapper imageLinkUrl={imageLinkUrl}>
          <Image
            naturalHeight={naturalHeight}
            naturalWidth={naturalWidth}
            backgroundColor={backgroundColor}
            objectFit={objectFit}
            objectPosition={objectPosition}
            src={isHovered && rolloverImageUrl ? rolloverImageUrl : src}
            alt={alt}
            lazy={lazy}
          />
        </ImageWrapper>
      </div>
    </div>
  );
};

UniversalImage.displayName = 'UniversalImage';

UniversalImage.defaultProps = {
  children: null,
  src: '',
  alt: 'universal image alt',
  naturalHeight: null,
  naturalWidth: null,
  lazy: false,
  backgroundColor: 'transparent',
  objectFit: 'contain',
  imageLinkUrl: '',
  rolloverImageUrl: '',
  jumpLink: '',
  outputSample: false,
  style: {},
  padding: {
    type: 'currency',
    currency: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    custom: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  },
  margin: {
    type: 'currency',
    currency: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    custom: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  },
  border: {
    type: 'none',
    borderWidth: 1,
    borderColor: '#cdcdcd',
    shadowColor: '',
    borderRounded: {
      type: 'currency',
      value: '0',
      currencyValue: 'border-radius-0',
    },
  },
  width: 'initial',
  height: 'initial',
};

UniversalImage.propTypes = {
  size: P.string,
  backgroundColor: P.string,
  children: P.node,
  imageLinkUrl: P.string,
  jumpLink: P.string,
  rolloverImageUrl: P.string,
  src: P.string,
  alt: P.string,
  naturalHeight: P.number,
  naturalWidth: P.number,
  lazy: P.bool,
  objectFit: P.oneOf(['none', 'fill', 'contain', 'cover', 'scale-down']),
  objectPosition: P.oneOf(['top', 'bottom', 'left', 'right', 'center']),
  outputSample: P.bool,
  style: P.object,
  padding: P.shape({
    type: P.string,
    currency: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
    custom: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
  }),
  margin: P.shape({
    type: P.string,
    currency: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
    custom: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
  }),
  border: P.shape({
    type: P.string,
    borderWidth: P.string,
    borderColor: P.string,
    shadowColor: P.string,
    borderRounded: P.shape({
      type: P.string,
      value: P.oneOfType([P.string, P.number]),
      currencyValue: P.string,
    }),
  }),
  width: P.string,
  height: P.string,
};

ImageWrapper.propTypes = {
  children: P.node,
  imageLinkUrl: P.string,
};

const isTestEnv = process?.env?.NODE_ENV;
/**
 *
 * @param {*} componentDocument
 * @param {string} fieldLabel
 * @param {Array<'padding'|'margin'|'border'>} allowedControls allowedControlsList List of controls to be shown in the editor
 * @param {string} fieldName
 * @param {object} options
 * @returns
 */
UniversalImage.getEditingConfig = (
  componentDocument,
  fieldLabel = 'Universal Image',
  allowedControls = isTestEnv ? ['border', 'margin', 'padding'] : [],
  fieldName = 'universalImage',
  options = {
    disableGroup: false,
  }
) => {
  const shouldDisplayCustomPadding =
    get(componentDocument, `props.${fieldName}.padding.type`, 'currency') ===
    'custom';
  const shouldDisplayCustomMargin =
    get(componentDocument, `props.${fieldName}.margin.type`, 'currency') ===
    'custom';

  const paddingType = get(
    componentDocument,
    `props.${fieldName}.padding.type`,
    'currency'
  );
  const marginType = get(
    componentDocument,
    `props.${fieldName}.margin.type`,
    'currency'
  );

  const paddingInputConfig = shouldDisplayCustomPadding
    ? buildSpacingInputs(
        numberInputSpec,
        `${fieldName}.padding.${paddingType}`,
        ['top', 'right', 'left', 'bottom'],
        '(pixels)'
      )
    : buildSpacingInputs(
        stringSelectSpec,
        `${fieldName}.padding.${paddingType}`,
        ['top', 'right', 'left', 'bottom'],
        null,
        !shouldDisplayCustomPadding
      );

  const paddingEditorControls = allowedControls.includes('padding')
    ? [
        group('Padding', [
          stringSelectSpec(
            `${fieldName}.padding.type`,
            'Padding Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...paddingInputConfig,
        ]),
      ]
    : [];

  const marginInputConfig = shouldDisplayCustomMargin
    ? buildSpacingInputs(
        numberInputSpec,
        `${fieldName}.margin.${marginType}`,
        ['top', 'right', 'left', 'bottom'],
        '(pixels)'
      )
    : buildSpacingInputs(
        stringSelectSpec,
        `${fieldName}.margin.${marginType}`,
        ['top', 'right', 'left', 'bottom'],
        null,
        !shouldDisplayCustomMargin
      );

  const marginEditorControls = allowedControls.includes('margin')
    ? [
        group('Margin', [
          stringSelectSpec(
            `${fieldName}.margin.type`,
            'Margin Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...marginInputConfig,
        ]),
      ]
    : [];

  const borderColorInputConfig =
    get(componentDocument, `props.${fieldName}.border.type`, 'none') === 'color'
      ? [
          stringInputSpec(
            `${fieldName}.border.borderColor`,
            'Border Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
          numberInputSpec(
            `${fieldName}.border.borderWidth`,
            'Border Width (eg: "2" - default is 0, change it to a higher value to be visible)'
          ),
        ]
      : [];

  const borderShadowInputConfig =
    get(componentDocument, `props.${fieldName}.border.type`, 'none') ===
    'shadow'
      ? [
          stringInputSpec(
            `${fieldName}.border.shadowColor`,
            'Drop Shadow Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
        ]
      : [];

  const currencyOrCustomRoundedBorderConfig =
    get(
      componentDocument,
      `props.${fieldName}.border.borderRounded.type`,
      'currency'
    ) === 'currency'
      ? [
          stringSelectSpec(
            `${fieldName}.border.borderRounded.currencyValue`,
            'Currency Value',
            [
              { label: 'None', value: '' },
              { label: 'border-radius-1', value: 'border-radius-1' },
              { label: 'border-radius-2', value: 'border-radius-2' },
            ],
            {
              help: 'Defaults to "none"',
            }
          ),
        ]
      : [
          numberInputSpec(
            `${fieldName}.border.borderRounded.value`,
            'Custom Value'
          ),
        ];

  const borderRoundedInputConfig =
    get(componentDocument, `props.${fieldName}.border.type`, 'none') ===
    'rounded'
      ? [
          stringSelectSpec(
            `${fieldName}.border.borderRounded.type`,
            'Border Rounded Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...currencyOrCustomRoundedBorderConfig,
        ]
      : [];

  const borderInputConfig = [
    group('Border', [
      stringSelectSpec(`${fieldName}.border.type`, 'Border', [
        { label: 'None', value: 'none' },
        { label: 'Rounded Corners', value: 'rounded' },
        { label: 'Border Color', value: 'color' },
        { label: 'Border Shadow', value: 'shadow' },
      ]),
      ...borderColorInputConfig,
      ...borderShadowInputConfig,
      ...borderRoundedInputConfig,
    ]),
  ];

  const borderEditorControls = allowedControls.includes('border')
    ? [...borderInputConfig]
    : [];

  const enableLazyLoadingInput =
    get(componentDocument, `props.${fieldName}.naturalHeight`, null) &&
    get(componentDocument, `props.${fieldName}.naturalWidth`, null);

  const editorPanelOptions = [
    stringInputSpec(`${fieldName}.src`, 'Image Source URL'),
    stringInputSpec(`${fieldName}.alt`, 'Image Alternate Text'),
    stringInputSpec(`${fieldName}.height`, 'Height'),
    stringInputSpec(`${fieldName}.width`, 'Width'),
    numberInputSpec(`${fieldName}.naturalHeight`, 'Image Natural Height'),
    numberInputSpec(`${fieldName}.naturalWidth`, 'Image Natural Width'),
    boolCheckboxSpec(`${fieldName}.lazy`, 'Image Lazy Loading', {
      help: 'Will be available only if "Image Natural Height" and "Image Natural Width" have been supplied',
      disabled: !enableLazyLoadingInput,
    }),

    group('Link', [
      stringInputSpec(`${fieldName}.imageLinkUrl`, 'Image Link URL'),
      stringInputSpec(`${fieldName}.rolloverImageUrl`, 'Rollover Image URL'),
    ]),
    group('Properties', [
      stringInputSpec(`${fieldName}.jumpLink`, 'Image Jump Link'),
      stringInputSpec(
        `${fieldName}.backgroundColor`,
        'Image Background Color',
        {
          help: 'A string representing the background color of the image. Defaults to "transparent"',
        }
      ),
      stringSelectSpec(`${fieldName}.objectFit`, 'Object Fit', [
        { label: 'Contain', value: 'contain' },
        { label: 'Fill', value: 'fill' },
        { label: 'None', value: 'none' },
        { label: 'Cover', value: 'cover' },
        { label: 'Scale Down', value: 'scale-down' },
      ]),
      stringSelectSpec(`${fieldName}.objectPosition`, 'Object Position', [
        { label: 'Top', value: 'top' },
        { label: 'Bottom', value: 'bottom' },
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
        { label: 'Center', value: 'center' },
      ]),
    ]),
    ...paddingEditorControls,
    ...marginEditorControls,
    ...borderEditorControls,
  ];

  return [
    options.disableGroup
      ? wrapper(editorPanelOptions)
      : group(fieldLabel, editorPanelOptions),
  ];
};

export default UniversalImage;
